import type {
  ChiTieuChildMismatchWarning,
  ChiTieuChildRef,
  ChiTieuChildUnitMismatch,
  ChiTieuScaleMismatch,
  ParentKpiChiTieuSnapshot,
} from '@/types/performance/Target/ChiTieuChildWarning';
import type { PerspectiveTableRow } from '@/types/performance/MeasureIndicators';
import CommonJs from '@/utils/commonJs';

export type ChiTieuScaleField = 'baseLine' | 'accept' | 'chiTieu' | 'max';

export type { ChiTieuChildMismatchWarning, ChiTieuChildRef as ChildChiTieuRef } from '@/types/performance/Target/ChiTieuChildWarning';

const FIELD_LABEL: Record<ChiTieuScaleField, string> = {
  baseLine: 'Min',
  accept: 'Ngưỡng chấp nhận',
  chiTieu: 'Chỉ tiêu',
  max: 'Max',
};

export const getChiTieuScaleFieldLabel = (field: ChiTieuScaleField | string): string =>
  FIELD_LABEL[field as ChiTieuScaleField] ?? field;

function parseNum(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function pick<T>(obj: Record<string, unknown> | null | undefined, ...keys: string[]): T | undefined {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k] as T;
  }
  return undefined;
}

/** Cột BoQuaCanhBaoChiTieuCon trên ListBoPhanCongTies — tắt cảnh báo chỉ tiêu con */
export function isChiTieuChildWarnDismissed(
  chiTieu?: { boQuaCanhBaoChiTieuCon?: boolean | null } | null
): boolean {
  return !!chiTieu?.boQuaCanhBaoChiTieuCon;
}

export function patchChiTieuChildWarningDismissInPayload(
  raw: unknown,
  kpiId: string,
  dismissed: boolean
): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const obj = raw as Record<string, unknown>;
  const items = (pick<Record<string, unknown>[]>(obj, 'items', 'Items') ?? []) as Record<string, unknown>[];
  const phuTrachWithChildren = (pick<string[]>(obj, 'phuTrachWithChildren', 'PhuTrachWithChildren') ?? []).map(String);
  return {
    ...obj,
    items: items.map((item) => {
      const id = String(pick(item, 'kpiId', 'KpiId') ?? '');
      if (id !== kpiId) return item;
      return { ...item, isDismissed: dismissed, IsDismissed: dismissed };
    }),
    phuTrachWithChildren,
  };
}

/** @deprecated Dùng getChildAggregateModeByHinhThuc — cảnh báo theo hình thức tính cha */
export function getChildAggregateMode(tiLeKPIChoices: string): 'sum' | 'avg' | 'single' {
  const t = (tiLeKPIChoices || '').toLowerCase();
  if (t === 'chung') return 'single';
  if (t.includes('trungbinh')) return 'avg';
  return 'sum';
}

/** 1 Tổng / 4 Tỉ lệ → sum; 2 Trung bình / 3 Tiến độ → từng con phải bằng cha */
export function isChiTieuSumCompare(hinhThuc: number): boolean {
  return hinhThuc === 1 || hinhThuc === 4;
}

export function getChildAggregateModeByHinhThuc(hinhThuc: number): 'sum' | 'each' {
  return isChiTieuSumCompare(hinhThuc) ? 'sum' : 'each';
}

export function getHinhThucLabel(hinhThuc: number): string {
  if (hinhThuc === 2) return 'Trung bình';
  if (hinhThuc === 3) return 'Tiến độ';
  if (hinhThuc === 4) return 'Tỉ lệ (2 thành phần)';
  return 'Tổng';
}

export function getChildAggregateLabelByHinhThuc(hinhThuc: number): string {
  return isChiTieuSumCompare(hinhThuc) ? 'Tổng KPI con' : 'Từng KPI con';
}

export function isScaleFieldMismatchedByHinhThuc(
  parentValue: number,
  childValues: number[],
  hinhThuc: number
): boolean {
  if (!childValues.length) return false;
  if (isChiTieuSumCompare(hinhThuc)) {
    const sum = childValues.reduce((s, v) => s + v, 0);
    return Math.abs(sum - parentValue) > 0.001;
  }
  return childValues.some((v) => Math.abs(v - parentValue) > 0.001);
}

export function sumChildScaleValues(childValues: number[]): number {
  return childValues.reduce((s, v) => s + v, 0);
}

export function buildParentKpiSnapshots(tableData: PerspectiveTableRow[]): ParentKpiChiTieuSnapshot[] {
  const result: ParentKpiChiTieuSnapshot[] = [];

  for (const pd of tableData) {
    for (const mt of pd.children || []) {
      const mucTieuName = mt.muctieuName || mt.name || '';
      for (const kpi of mt.children || []) {
        if (kpi.type !== 'kpi') continue;
        if (kpi.chiTieu?.boKPI) continue;

        result.push({
          kpiId: String(kpi.kpiID || kpi.id),
          kpiName: kpi.kpiName || '',
          mucTieuName,
          baseLine: parseNum(kpi.baseLine ?? kpi.chiTieu?.baseLine),
          accept: parseNum(kpi.accept ?? kpi.chiTieu?.accept),
          chiTieu: parseNum(kpi.chiTieuValue ?? kpi.chiTieu?.chiTieu),
          max: parseNum(kpi.max ?? kpi.chiTieu?.max),
          idDoLuong: String(kpi.iddoluong ?? kpi.chiTieu?.idDoLuong ?? ''),
          tiLeKPIChoices: kpi.tiLeKPIChoices || kpi.chiTieu?.tiLeKPIChoices || '',
          hinhThuc: parseNum(kpi.chiTieu?.hinhThuc) || 1,
          boQuaCanhBaoChiTieuCon: !!kpi.chiTieu?.boQuaCanhBaoChiTieuCon,
          boKPI: !!kpi.chiTieu?.boKPI,
        });
      }
    }
  }

  return result;
}

function mapScaleMismatch(raw: Record<string, unknown>): ChiTieuScaleMismatch {
  return {
    field: String(pick(raw, 'field', 'Field') ?? ''),
    parentValue: parseNum(pick(raw, 'parentValue', 'ParentValue')),
    childAggregateValue: CommonJs.parseDecimal(parseNum(pick(raw, 'childAggregateValue', 'ChildAggregateValue')), 3),
    hinhThucMismatch: !!pick<boolean>(raw, 'hinhThucMismatch', 'HinhThucMismatch'),
  };
}

function mapUnitMismatch(raw: Record<string, unknown>): ChiTieuChildUnitMismatch {
  return {
    chiTieuId: String(pick(raw, 'chiTieuId', 'ChiTieuId') ?? ''),
    phuTrachId: String(pick(raw, 'phuTrachId', 'PhuTrachId') ?? ''),
    unitId: String(pick(raw, 'unitId', 'UnitId') ?? ''),
    unitName: String(pick(raw, 'unitName', 'UnitName') ?? ''),
  };
}

function mapChildRef(raw: Record<string, unknown>): ChiTieuChildRef {
  return {
    chiTieuId: String(pick(raw, 'chiTieuId', 'ChiTieuId') ?? ''),
    phuTrachId: String(pick(raw, 'phuTrachId', 'PhuTrachId') ?? ''),
    phuTrachName: String(pick(raw, 'phuTrachName', 'PhuTrachName') ?? ''),
    kpiName: String(pick(raw, 'kpiName', 'KpiName') ?? ''),
    mucTieuName: String(pick(raw, 'mucTieuName', 'MucTieuName') ?? ''),
    baseLine: parseNum(pick(raw, 'baseLine', 'BaseLine')),
    accept: parseNum(pick(raw, 'accept', 'Accept')),
    chiTieu: parseNum(pick(raw, 'chiTieu', 'ChiTieu')),
    max: parseNum(pick(raw, 'max', 'Max')),
    hinhThuc: parseNum(pick(raw, 'hinhThuc', 'HinhThuc')) || 1,
    unitId: String(pick(raw, 'unitId', 'UnitId') ?? ''),
    unitName: String(pick(raw, 'unitName', 'UnitName') ?? ''),
  };
}

function mapWarningItem(raw: Record<string, unknown>): ChiTieuChildMismatchWarning {
  const tiLe = String(pick(raw, 'tiLeKPIChoices', 'TiLeKPIChoices') ?? '');
  const scaleRaw = (pick<Record<string, unknown>[]>(raw, 'scaleMismatches', 'ScaleMismatches') ?? []) as Record<
    string,
    unknown
  >[];
  const unitRaw = (pick<Record<string, unknown>[]>(raw, 'unitMismatches', 'UnitMismatches') ?? []) as Record<
    string,
    unknown
  >[];
  const childrenRaw = (pick<Record<string, unknown>[]>(raw, 'children', 'Children') ?? []) as Record<
    string,
    unknown
  >[];

  return {
    phuTrachId: String(pick(raw, 'phuTrachId', 'PhuTrachId') ?? ''),
    phuTrachName: String(pick(raw, 'phuTrachName', 'PhuTrachName') ?? ''),
    kpiId: String(pick(raw, 'kpiId', 'KpiId') ?? ''),
    kpiName: String(pick(raw, 'kpiName', 'KpiName') ?? ''),
    mucTieuName: String(pick(raw, 'mucTieuName', 'MucTieuName') ?? ''),
    tiLeKPIChoices: tiLe,
    hinhThuc: parseNum(pick(raw, 'hinhThuc', 'HinhThuc')) || 1,
    aggregateMode: getChildAggregateModeByHinhThuc(parseNum(pick(raw, 'hinhThuc', 'HinhThuc')) || 1),
    childCount: parseNum(pick(raw, 'childCount', 'ChildCount')),
    unitMismatch: !!pick<boolean>(raw, 'unitMismatch', 'UnitMismatch'),
    isDismissed: !!pick<boolean>(raw, 'isDismissed', 'IsDismissed'),
    parentUnitId: String(pick(raw, 'parentUnitId', 'ParentUnitId') ?? ''),
    parentUnitName: String(pick(raw, 'parentUnitName', 'ParentUnitName') ?? ''),
    baseLine: parseNum(pick(raw, 'baseLine', 'BaseLine')),
    accept: parseNum(pick(raw, 'accept', 'Accept')),
    chiTieu: parseNum(pick(raw, 'chiTieu', 'ChiTieu')),
    max: parseNum(pick(raw, 'max', 'Max')),
    scaleMismatches: scaleRaw.map(mapScaleMismatch),
    unitMismatches: unitRaw.map(mapUnitMismatch),
    children: childrenRaw.map(mapChildRef),
  };
}

export function mapChiTieuChildWarningResponse(raw: unknown): ChiTieuChildMismatchWarning[] {
  return parseChiTieuChildWarningPayload(raw).items;
}

export function parseChiTieuChildWarningPayload(raw: unknown): {
  items: ChiTieuChildMismatchWarning[];
  phuTrachWithChildren: string[];
} {
  if (!raw || typeof raw !== 'object') {
    return { items: [], phuTrachWithChildren: [] };
  }
  const obj = raw as Record<string, unknown>;
  const items = (pick<Record<string, unknown>[]>(obj, 'items', 'Items') ?? []) as Record<string, unknown>[];
  const phuTrachWithChildren = (pick<string[]>(obj, 'phuTrachWithChildren', 'PhuTrachWithChildren') ?? []).map(String);
  return {
    items: items.map(mapWarningItem),
    phuTrachWithChildren,
  };
}

export function getWarningScaleValue(
  warning: Pick<ChiTieuChildMismatchWarning, 'baseLine' | 'accept' | 'chiTieu' | 'max'>,
  field: ChiTieuScaleField
): number {
  if (field === 'baseLine') return warning.baseLine;
  if (field === 'accept') return warning.accept;
  if (field === 'chiTieu') return warning.chiTieu;
  return warning.max;
}

export function getChildRefScaleValue(child: ChiTieuChildRef, field: ChiTieuScaleField): number {
  if (field === 'baseLine') return child.baseLine;
  if (field === 'accept') return child.accept;
  if (field === 'chiTieu') return child.chiTieu;
  return child.max;
}

export function listMismatchedScaleFields(
  warning: ChiTieuChildMismatchWarning
): ChiTieuScaleField[] {
  const fields: ChiTieuScaleField[] = ['baseLine', 'accept', 'chiTieu', 'max'];
  return fields.filter((field) => {
    const childValues = (warning.children || []).map((child) => getChildRefScaleValue(child, field) ?? 0);
    return isScaleFieldMismatchedByHinhThuc(getWarningScaleValue(warning, field), childValues, warning.hinhThuc);
  });
}

export function buildChiTieuChildWarningsMap(
  warnings: ChiTieuChildMismatchWarning[],
  phuTrachId?: string
): Map<string, ChiTieuChildMismatchWarning> {
  const filtered = phuTrachId ? warnings.filter((w) => w.phuTrachId === phuTrachId) : warnings;
  return new Map(filtered.map((w) => [w.kpiId, w]));
}

export interface ChiTieuChaConTreeRow {
  key: string;
  role: 'parent' | 'child';
  kpiId: string;
  mucTieuName: string;
  kpiName: string;
  phuTrachId: string;
  phuTrachName: string;
  unitName: string;
  unitId?: string;
  baseLine: number;
  accept: number;
  chiTieu: number;
  max: number;
  hinhThuc: number;
  unitMismatch: boolean;
  scaleMismatches: ChiTieuScaleMismatch[];
  childCount: number;
  parentBaseLine?: number;
  parentAccept?: number;
  parentChiTieu?: number;
  parentMax?: number;
  parentHinhThuc?: number;
  children?: ChiTieuChaConTreeRow[];
}

function parentScaleValue(
  warning: ChiTieuChildMismatchWarning,
  field: ChiTieuScaleField,
  direct: number
): number {
  return direct || warning.scaleMismatches.find((m) => m.field === field)?.parentValue || 0;
}

export function buildChiTieuChaConTreeRows(
  warnings: ChiTieuChildMismatchWarning[]
): ChiTieuChaConTreeRow[] {
  return warnings.map((warning) => {
    const parentBaseLine = parentScaleValue(warning, 'baseLine', warning.baseLine);
    const parentAccept = parentScaleValue(warning, 'accept', warning.accept);
    const parentChiTieu = parentScaleValue(warning, 'chiTieu', warning.chiTieu);
    const parentMax = parentScaleValue(warning, 'max', warning.max);

    const children: ChiTieuChaConTreeRow[] = (warning.children || []).map((child, index) => ({
      key: `${warning.kpiId}-${child.chiTieuId || index}`,
      role: 'child',
      kpiId: child.chiTieuId || `${warning.kpiId}-child-${index}`,
      mucTieuName: child.mucTieuName || warning.mucTieuName,
      kpiName: child.kpiName || warning.kpiName,
      phuTrachId: child.phuTrachId || '',
      phuTrachName: child.phuTrachName || '',
      unitName: child.unitName || '',
      unitId: child.unitId,
      baseLine: child.baseLine ?? 0,
      accept: child.accept ?? 0,
      chiTieu: child.chiTieu ?? 0,
      max: child.max ?? 0,
      hinhThuc: child.hinhThuc || warning.hinhThuc,
      unitMismatch: warning.unitMismatches.some((u) => u.chiTieuId === child.chiTieuId),
      scaleMismatches: warning.scaleMismatches,
      childCount: 0,
      parentBaseLine,
      parentAccept,
      parentChiTieu,
      parentMax,
      parentHinhThuc: warning.hinhThuc,
    }));

    return {
      key: warning.kpiId,
      role: 'parent',
      kpiId: warning.kpiId,
      mucTieuName: warning.mucTieuName,
      kpiName: warning.kpiName,
      phuTrachId: warning.phuTrachId,
      phuTrachName: warning.phuTrachName || '',
      unitName: warning.parentUnitName,
      unitId: warning.parentUnitId,
      baseLine: parentBaseLine,
      accept: parentAccept,
      chiTieu: parentChiTieu,
      max: parentMax,
      hinhThuc: warning.hinhThuc,
      unitMismatch: warning.unitMismatch,
      scaleMismatches: warning.scaleMismatches,
      childCount: warning.childCount || children.length,
      parentHinhThuc: warning.hinhThuc,
      children: children.length ? children : undefined,
    };
  });
}
